import Card from "./Card";
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useFilmDetail } from "../hooks/useFilmDetail";
import { posterAtWidth } from "../lib/poster";
import StarRating from "./StarRating";
import NotFound from "./NotFound";
import type { FilmRater } from "../../shared/types";
import { Notification } from "./ui/Notification";
import { SITE_NAME } from "./routeTitles";

const POSTER_WIDTH = 320;

const StatTile = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-row items-center">
    <dd className="text-2xl text-letterboxd-text-primary tabular-nums font-bold">
      {value}
    </dd>
    <dt className="ml-2 text-letterboxd-text-muted text-sm">{label}</dt>
  </div>
);

const RaterRow = ({ rater }: { rater: FilmRater }) => (
  <li className="flex items-center justify-between gap-3 py-2 border-b border-letterboxd-border">
    <Link
      to={`/user/${rater.username}`}
      className="text-letterboxd-text-primary hover:text-letterboxd-accent truncate"
    >
      {rater.displayName || rater.username}
    </Link>
    <span className="flex items-center gap-2 shrink-0">
      {rater.liked && (
        <span role="img" aria-label="liked" className="text-letterboxd-accent">
          ♥
        </span>
      )}
      <StarRating rating={rater.rating} />
      <span className="text-sm tabular-nums text-letterboxd-text-secondary">
        {rater.rating.toFixed(1)}
      </span>
    </span>
  </li>
);

const FilmPage = () => {
  const { filmSlug = "" } = useParams();
  const [searchParams] = useSearchParams();
  const includeNonDiscord = searchParams.get("includeNonDiscord") === "true";
  const { data, loading, error, notFound } = useFilmDetail(
    filmSlug,
    includeNonDiscord,
  );
  // Keyed by URL, not a boolean: one film's missing crop must not disable
  // upscaling for every film visited afterwards.
  const [failedPoster, setFailedPoster] = useState<string | null>(null);

  // MainLayout seeds document.title from the slug on navigation; the real title
  // only exists once the fetch resolves, so override it here.
  useEffect(() => {
    if (data?.title) document.title = `${data.title} — ${SITE_NAME}`;
  }, [data?.title]);

  if (loading) {
    return (
      <Card className="text-letterboxd-text-muted text-sm" aria-busy="true">
        Loading film…
      </Card>
    );
  }

  if (notFound) {
    return (
      <NotFound
        title={`No film for "${filmSlug}"`}
        message="We don't have Letterboxd data for this film yet. It may need to be added and refreshed."
      />
    );
  }

  if (error || !data) {
    return (
      <Notification
        status={{ type: "error", message: error ?? "Failed to load film" }}
      />
    );
  }

  const letterboxdUrl =
    data.letterboxdUrl ?? `https://letterboxd.com/film/${data.filmSlug}/`;
  // Falls back to the stored (smaller) poster if a resized crop doesn't exist.
  const upscale = data.poster && failedPoster !== data.poster;
  const posterSrc = upscale
    ? posterAtWidth(data.poster!, POSTER_WIDTH)
    : data.poster;
  const posterSrcSet = upscale
    ? `${posterAtWidth(data.poster!, 460)} 460w, ${posterAtWidth(data.poster!, 600)} 600w`
    : undefined;

  const emptyRatings =
    data.watchedCount === 0
      ? "Nobody in the Discord has logged this film yet."
      : `No ratings yet — ${data.watchedCount} ${
          data.watchedCount === 1 ? "person has" : "people have"
        } logged it.`;

  return (
    <div className="space-y-8">
      <Card className="flex flex-col md:flex-row items-center md:items-start gap-6">
        <div className="w-half flex-grow-0 grid grid-cols-[7rem_1fr] items-start gap-4 sm:grid-cols-[9rem_1fr] md:block md:self-start md:sticky md:top-6">
          <a
            href={letterboxdUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`View ${data.title} on Letterboxd`}
            className="block w-full max-w-[320px] shrink-0 transition-transform duration-300 hover:scale-[1.02]"
          >
            {posterSrc ? (
              <img
                src={posterSrc}
                srcSet={posterSrcSet}
                sizes={`${POSTER_WIDTH}px`}
                alt={`${data.title} poster`}
                width={POSTER_WIDTH}
                height={POSTER_WIDTH * 1.5}
                onError={() => setFailedPoster(data.poster)}
                className="w-full aspect-[2/3] object-cover rounded"
              />
            ) : (
              <div className="flex w-full aspect-[2/3] items-center justify-center rounded bg-letterboxd-bg-tertiary text-sm text-letterboxd-text-muted">
                No poster
              </div>
            )}
          </a>
          <dl className="grid grid-rows-4 gap-4 md:mt-6">
            <StatTile
              label="watched"
              value={data.watchedCount.toLocaleString()}
            />
            <StatTile label="rated" value={data.ratedCount.toLocaleString()} />
            <StatTile
              label="Our average"
              value={data.averageRating?.toFixed(2) ?? "—"}
            />
            <StatTile
              label="Letterboxd average"
              value={data.letterboxdRating?.toFixed(2) ?? "—"}
            />
          </dl>
        </div>
        <div className="w-half min-w-0 flex-1">
          <h1 className="text-2xl md:text-4xl font-bold text-letterboxd-text-primary">
            {data.title}
          </h1>
          <p className="letterboxd-text-secondary text-sm">
            {data.releaseYear}
          </p>

          <h2 className="subheading mt-4">How we rated it</h2>
          {data.ratings.length > 0 ? (
            <ul className="grid grid-cols-1 gap-x-8">
              {data.ratings.map((rater) => (
                <RaterRow key={rater.username} rater={rater} />
              ))}
            </ul>
          ) : (
            <p className="body-text -prose italic opacity-70">{emptyRatings}</p>
          )}
        </div>
      </Card>
    </div>
  );
};

export default FilmPage;
